"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";

const THEME = {
  DARK: "dark",
  LIGHT: "light",
} as const;

type Theme = (typeof THEME)[keyof typeof THEME];

const DEFAULT_THEME: Theme = THEME.LIGHT;

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

const STORAGE_KEY = "cos-theme";

export function ThemeProvider({
  children,
  initialTheme,
}: {
  children: React.ReactNode;
  initialTheme?: Theme;
}) {
  // Use a consistent default for BOTH server and initial client render
  // to prevent hydration mismatch. The flash prevention script in layout.tsx
  // handles the visual appearance; React state syncs after mount.
  const [theme, setThemeState] = useState<Theme>(initialTheme ?? DEFAULT_THEME);

  // Track whether initial mount sync has completed. This prevents the
  // DOM-sync useEffect from firing with the stale "light" default and
  // overwriting the flash prevention script's correct class state.
  const mountedRef = useRef(false);

  const setTheme = (next: Theme) => {
    setThemeState(next);
    document.documentElement.classList.toggle("dark", next === THEME.DARK);
    localStorage.setItem(STORAGE_KEY, next);
  };

  const toggleTheme = () => {
    setTheme(theme === THEME.DARK ? THEME.LIGHT : THEME.DARK);
  };

  // Detect actual theme preference AFTER hydration to avoid mismatch.
  // The flash prevention script already set the correct DOM class,
  // so here we READ from localStorage (source of truth) and sync React
  // state to match — without touching the DOM class unnecessarily.
  useEffect(() => {
    if (initialTheme) {
      mountedRef.current = true;
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;

    if (stored === THEME.DARK || stored === THEME.LIGHT) {
      // localStorage is the source of truth — flash script already
      // applied the correct DOM class from this same value.
      setThemeState(stored);

      // Defensive sync: only touch DOM if it disagrees with stored value
      const isDark = document.documentElement.classList.contains("dark");
      if (stored === THEME.DARK && !isDark) {
        document.documentElement.classList.add("dark");
      } else if (stored === THEME.LIGHT && isDark) {
        document.documentElement.classList.remove("dark");
      }
    } else {
      // No stored preference — use what flash script decided
      // (based on prefers-color-scheme)
      const isDark = document.documentElement.classList.contains("dark");
      setThemeState(isDark ? THEME.DARK : THEME.LIGHT);
    }

    mountedRef.current = true;
  }, [initialTheme]);

  // Keep DOM class in sync when theme changes AFTER mount.
  // Skips the first run to avoid overriding the flash prevention script
  // with the stale SSR default ("light").
  useEffect(() => {
    if (!mountedRef.current) return;
    document.documentElement.classList.toggle("dark", theme === THEME.DARK);
  }, [theme]);

  return (
    <ThemeContext value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
